// Utilidades, constantes y tipos compartidos del dashboard (sin JSX).
import { Activity } from "./types";

export const CATS = ["Carrera", "Ciclismo", "Natacion", "Hybrid", "Hike", "Musculacion", "Otros"] as const;
export type Cat = (typeof CATS)[number];

/** Categorías que son "disciplinas" y pueden tener su propia tab (Otros nunca: es el cajón
 * de sobras, solo visible en el reparto del Resumen). El orden fija el de las tabs dinámicas.
 * Musculacion se incluye para que genere tab pero el Dashboard renderiza BodyComposition, no Discipline. */
export const DISCIPLINES = ["Carrera", "Ciclismo", "Natacion", "Hybrid", "Hike", "Musculacion"] as const;
export type Discipline = (typeof DISCIPLINES)[number];

/** Categorías presentes en un conjunto de actividades, ordenadas por nº de sesiones desc. */
export function catsByActivityCount(acts: Activity[]): Cat[] {
  const counts = new Map<Cat, number>();
  for (const a of acts) {
    if ((CATS as readonly string[]).includes(a.categoria)) {
      counts.set(a.categoria, (counts.get(a.categoria) ?? 0) + 1);
    }
  }
  return [...counts.keys()].sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0));
}

/** Disciplinas con tab potencial, ordenadas por nº de sesiones desc. `Otros` nunca tiene tab. */
export function disciplinesByActivityCount(acts: Activity[]): Discipline[] {
  return catsByActivityCount(acts).filter((c): c is Discipline =>
    (DISCIPLINES as readonly string[]).includes(c),
  );
}

/** Una disciplina "de distancia" muestra km y ritmo; "Hybrid" (fuerza/funcional) no. */
export const isDistanceDiscipline = (c: Discipline): boolean => c !== "Hybrid";

/** Tipo de tendencia de "velocidad" según la disciplina: ritmo (min/km), velocidad (km/h) o
 * ritmo de natación (s/100m). Hybrid no tiene (es fuerza, sin distancia). */
export type TrendKind = "pace" | "speed" | "pace100";

export function trendKindOf(c: Discipline): TrendKind | null {
  if (c === "Hybrid") return null;
  if (c === "Ciclismo") return "speed";
  if (c === "Natacion") return "pace100";
  return "pace"; // Carrera, Hike (senderismo)
}

/** Valor del punto de la tendencia para una actividad, según el tipo:
 *  - pace: min/km (ya enriquecido en el backend)
 *  - speed: km/h (de avg_speed m/s)
 *  - pace100: s/100m (de avg_speed m/s) */
export function trendValue(a: Activity, kind: TrendKind): number | null {
  if (kind === "pace") return a.pace_min_km;
  if (!a.avg_speed || a.avg_speed <= 0) return null;
  return kind === "speed" ? a.avg_speed * 3.6 : 100 / a.avg_speed;
}

/** El eje del ritmo se invierte (menos es mejor); el de velocidad no. */
export const trendReversed = (kind: TrendKind): boolean => kind !== "speed";

export const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Parsea 'YYYY-MM-DD' como hora LOCAL (coherente con start_local). */
export const day = (s: string) => new Date(s + "T00:00:00");

/** Strava referencia el material como 'g123'/'b123' pero la tabla guarda '123'. */
export const normGearId = (id: string) => id.replace(/^[gb](?=\d)/i, "");

export const pad2 = (n: number) => String(n).padStart(2, "0");

/** Lunes (inicio de semana ISO) de la fecha dada, como 'YYYY-MM-DD'. */
export function weekStartOf(iso: string): string {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7; // 0 = lunes
  d.setDate(d.getDate() - dow);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Granularidad de los gráficos de seguimiento: barra por día o por semana. */
export type Granularity = "day" | "week";

/** Clave de bucket ('YYYY-MM-DD') de una fecha según la granularidad: el día
 * exacto, o el lunes de su semana. */
export function bucketOf(iso: string, g: Granularity): string {
  return g === "week" ? weekStartOf(iso) : iso.slice(0, 10);
}

export const round2 = (n: number) => Math.round(n * 100) / 100;

export const isoOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export function rgba(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export const HEAT_OPACITY = [0, 0.3, 0.52, 0.74, 1]; // niveles 0..4
export function heatLevel(mins: number) {
  if (mins <= 0) return 0;
  if (mins < 30) return 1;
  if (mins < 60) return 2;
  if (mins < 90) return 3;
  return 4;
}
export const CELL = 26; // tamaño de celda (px), grande para llenar la tarjeta

export interface Period {
  start: Date;
  end: Date;
  label: string;
}

/** Definición de una métrica seleccionable en el gráfico de seguimiento. */
export type Metric = {
  key: string;
  label: string;
  agg: "count" | "sum" | "avg";
  field?: (a: Activity) => number | null;
  fmt: (v: number | null, loc: string) => string;
  color: string;
};

export function aggWeek(acts: Activity[], m: Metric): number | null {
  if (m.agg === "count") return acts.length;
  const vals = acts
    .map((a) => (m.field ? m.field(a) : null))
    .filter((v): v is number => v != null && !Number.isNaN(v));
  if (!vals.length) return m.agg === "avg" ? null : 0;
  const sum = vals.reduce((s, v) => s + v, 0);
  return m.agg === "avg" ? sum / vals.length : sum;
}
